// Payload CMS API

const PAYLOAD_URL =
  process.env.NEXT_PUBLIC_PAYLOAD_URL || "http://localhost:3080";

function mapLocale(lang) {
  const map = {
    "en-gb": "en",
    en: "en",
    ru: "ru",
    "es-la": "es-la",
    "pt-br": "pt-br",
    it: "it",
    fr: "fr",
    de: "de",
  };
  return map[lang] || "en";
}

async function apiFetch(path, { draft = false } = {}) {
  const headers = {};
  if (draft && process.env.PREVIEW_SECRET) {
    headers.Authorization = `Bearer ${process.env.PREVIEW_SECRET}`;
  }
  const separator = path.includes("?") ? "&" : "?";
  const draftParam = draft ? `${separator}draft=true` : "";
  const res = await fetch(`${PAYLOAD_URL}/api${path}${draftParam}`, {
    headers,
  });
  if (!res.ok) throw new Error(`Payload API ${res.status}: ${path}`);
  return res.json();
}

// ---------------------------------------------------------------------------
// Lexical JSON utilities
// ---------------------------------------------------------------------------

function lexicalToPlainText(content) {
  if (!content?.root?.children) return "";
  const parts = [];
  for (const child of content.root.children) {
    if (child.type === "paragraph" || child.type === "heading") {
      parts.push((child.children || []).map((c) => c.text || "").join(""));
    } else if (child.type === "list") {
      for (const item of child.children || []) {
        parts.push((item.children || []).map((c) => c.text || "").join(""));
      }
    }
  }
  return parts.filter(Boolean).join(" ");
}

// ---------------------------------------------------------------------------
// Document transformers
// ---------------------------------------------------------------------------

function mediaShape(file) {
  const rawUrl = file?.url || null;
  // Payload serves media via its own /api/media/file/ endpoint; make it absolute
  let url =
    rawUrl && rawUrl.startsWith("/") ? `${PAYLOAD_URL}${rawUrl}` : rawUrl;

  if (url && url.includes("+")) {
    url = url.replace(/\+/g, "%2B");
  }

  return {
    url,
    alt: file?.alt || "",
    dimensions:
      file?.width && file?.height
        ? { width: file.width, height: file.height }
        : null,
  };
}

export function transformArticle(doc) {
  const thumbnail = mediaShape(doc.thumbnail);
  return {
    id: doc.id,
    uid: doc.slug,
    type: "article",
    first_publication_date: doc.publishedAt,
    last_publication_date: doc.updatedAt,
    tags: (doc.tags || [])
      .map((t) => (typeof t === "object" ? t.value || t.id : t))
      .filter(Boolean),
    data: {
      title: [{ text: doc.title || "" }],
      published_date: doc.publishedAt,
      meta_title: doc.meta?.title || doc.title || "",
      meta_description: doc.meta?.description || "",
      thumbnail,
      feature_image: doc.feature?.image
        ? mediaShape(doc.feature.image)
        : thumbnail,
      introduction: doc.introduction || null,
      feature_video: doc.feature?.video || null,
      feature_video_position: doc.feature?.videoPosition || null,
      body: (doc.body || []).map((block) => {
        if (block.blockType !== "image") return block;
        return { ...block, image: mediaShape(block.image) };
      }),
      related_articles: (doc.relatedArticles || [])
        .filter((rel) => rel?.slug)
        .map((rel) => ({
          article: {
            uid: rel.slug,
            data: {
              title: [{ text: rel.title || "" }],
              thumbnail: mediaShape(rel.thumbnail),
              published_date: rel.publishedAt,
            },
          },
        })),
    },
  };
}

export function transformEpisode(doc) {
  return {
    id: doc.id,
    uid: doc.slug,
    type: "episode",
    first_publication_date: doc.publishedAt,
    last_publication_date: doc.updatedAt,
    tags: (doc.tags || [])
      .map((t) => (typeof t === "object" ? t.value || t.id : t))
      .filter(Boolean),
    data: {
      title: [{ text: doc.title || "" }],
      published_date: doc.publishedAt,
      description: lexicalToPlainText(doc.description),
      thumbnail: mediaShape(doc.thumbnail),
      feature_video: doc.featureVideo || null,
      episode_number: doc.episode_number || null,
      time: doc.time || null,
      meta_title: doc.meta?.title || doc.title || "",
      meta_description: doc.meta?.description || "",
    },
  };
}

function transformJobPost(doc) {
  return {
    id: doc.id,
    uid: doc.slug,
    data: {
      title: [{ text: doc.title || "" }],
      published_date: doc.publishedAt,
      category: doc.department || "",
      work_type: "",
      location: "",
      description: doc.description || null,
      key_qualifications: [],
      responsibilities: doc.requirements || null,
      additional_requirements: [],
      our_company: [],
      our_industry: [],
      salary_range: [],
      reference: null,
      body: [],
      meta_title: doc.title || "",
      meta_description: "",
    },
  };
}

function transformEvent(doc) {
  return {
    id: doc.id,
    uid: doc.slug,
    last_publication_date: doc.updatedAt,
    data: {
      title: [{ text: doc.title || "" }],
      date: doc.date,
      display_date: doc.date,
      location: doc.location || "",
      location_2_line: null,
      location_country: null,
      description: lexicalToPlainText(doc.description),
      // EventPostItem accesses thumbnail without null check — provide empty fallback
      thumbnail: { url: null, alt: "" },
      link_details: null,
      link_register: null,
      booth: null,
    },
  };
}

function transformSneakpeek(doc) {
  return {
    id: doc.id,
    uid: doc.slug || String(doc.id),
    data: {
      video_id: doc.videoId || null,
      name: doc.title || null,
    },
  };
}

function transformVideoSection(doc) {
  return {
    id: doc.id,
    uid: doc.slug,
    data: {
      playback_id: doc.playbackId || null,
      title: doc.title || "",
      poster: null,
      chapter_element: (doc.chapter_element || []).map((ch) => ({
        title: ch.title || "",
        start_time: ch.start_time ?? null,
        end_time: ch.end_time ?? null,
        thumbnail: mediaShape(ch.thumbnail),
      })),
    },
  };
}

function toPaginated(payloadRes, transformFn) {
  return {
    results: (payloadRes.docs || []).map(transformFn),
    page: payloadRes.page || 1,
    total_pages: payloadRes.totalPages || 1,
    results_per_page: payloadRes.limit || 20,
    total_results_size: payloadRes.totalDocs || 0,
  };
}

// ---------------------------------------------------------------------------
// Exported API
// ---------------------------------------------------------------------------

export async function getAllPosts(lang = "en-gb") {
  const locale = mapLocale(lang);
  const data = await apiFetch(
    `/articles?locale=${locale}&sort=-publishedAt&limit=100&depth=1`,
  );
  return toPaginated(data, transformArticle);
}

export async function getPostsPage(page, lang = "en-gb") {
  const locale = mapLocale(lang);
  const data = await apiFetch(
    `/articles?locale=${locale}&sort=-publishedAt&limit=19&page=${page}&depth=1`,
  );
  return toPaginated(data, transformArticle);
}

export async function getRecentPosts(lang = "en-gb") {
  const locale = mapLocale(lang);
  const data = await apiFetch(
    `/articles?locale=${locale}&sort=-publishedAt&limit=3&depth=1`,
  );
  return toPaginated(data, transformArticle);
}

export async function getSearchedPosts(lang = "en-gb", searchQuery) {
  const locale = mapLocale(lang);
  const q = encodeURIComponent(searchQuery.trim());
  const data = await apiFetch(
    `/articles?locale=${locale}&sort=-publishedAt&limit=20&depth=1&where[title][contains]=${q}`,
  );
  return toPaginated(data, transformArticle);
}

export async function getSearchedBlogContent(lang = "en-gb", searchQuery) {
  const locale = mapLocale(lang);
  const q = encodeURIComponent(searchQuery.trim());
  const [articles, episodes] = await Promise.all([
    apiFetch(
      `/articles?locale=${locale}&limit=100&depth=1&where[or][0][title][like]=${q}&where[or][1][meta.description][like]=${q}`,
    ),
    apiFetch(
      `/episodes?locale=${locale}&limit=100&depth=1&where[or][0][title][like]=${q}&where[or][1][meta.description][like]=${q}`,
    ),
  ]);
  const results = [
    ...(articles.docs || []).map(transformArticle),
    ...(episodes.docs || []).map(transformEpisode),
  ].sort(
    (a, b) =>
      new Date(b.first_publication_date) - new Date(a.first_publication_date),
  );
  return {
    results,
    page: 1,
    total_pages: 1,
    results_per_page: 20,
    total_results_size: results.length,
  };
}

export async function getPost(uid, lang = "en-gb", _ref, preview = false) {
  const locale = mapLocale(lang);
  const data = await apiFetch(
    `/articles?locale=${locale}&where[slug][equals]=${encodeURIComponent(uid)}&limit=1&depth=2`,
    { draft: preview },
  );
  const doc = data.docs?.[0];
  if (!doc) throw new Error(`Article not found: ${uid}`);
  return transformArticle(doc);
}

export async function getPostRaw(uid, lang = "en-gb", preview = false) {
  const locale = mapLocale(lang);
  const data = await apiFetch(
    `/articles?locale=${locale}&where[slug][equals]=${encodeURIComponent(uid)}&limit=1&depth=2`,
    { draft: preview },
  );
  const doc = data.docs?.[0];
  if (!doc) throw new Error(`Article not found: ${uid}`);
  return doc;
}

export async function getLastSneakpeek() {
  const data = await apiFetch("/sneakpeeks?sort=-publishedAt&limit=1");
  return toPaginated(data, transformSneakpeek);
}

export async function getJobPosts(page = 1) {
  const data = await apiFetch(
    `/job-posts?sort=-publishedAt&limit=20&page=${page}`,
  );
  return toPaginated(data, transformJobPost);
}

export async function getJobPost(uid) {
  const data = await apiFetch(
    `/job-posts?where[slug][equals]=${encodeURIComponent(uid)}&limit=1&depth=1`,
  );
  const doc = data.docs?.[0];
  if (!doc) throw new Error(`Job post not found: ${uid}`);
  return transformJobPost(doc);
}

export async function getEpisodesPage(page, lang = "en-gb") {
  const locale = mapLocale(lang);
  const data = await apiFetch(
    `/episodes?locale=${locale}&sort=-publishedAt&limit=18&page=${page}&depth=1`,
  );
  return toPaginated(data, transformEpisode);
}

export async function getAllEpisodes(lang = "en-gb") {
  const locale = mapLocale(lang);
  const data = await apiFetch(
    `/episodes?locale=${locale}&sort=-publishedAt&limit=100&depth=1`,
  );
  return toPaginated(data, transformEpisode);
}

export async function getEpisodeRaw(uid, lang = "en-gb", preview = false) {
  const locale = mapLocale(lang);
  const data = await apiFetch(
    `/episodes?locale=${locale}&where[slug][equals]=${encodeURIComponent(uid)}&limit=1&depth=2`,
    { draft: preview },
  );
  const doc = data.docs?.[0];
  if (!doc) throw new Error(`Episode not found: ${uid}`);
  return doc;
}

export async function getRecentEpisodes(lang = "en-gb") {
  const locale = mapLocale(lang);
  const data = await apiFetch(
    `/episodes?locale=${locale}&sort=-publishedAt&limit=3&depth=1`,
  );
  return toPaginated(data, transformEpisode);
}

export async function getFaqs(lang = "en-gb", page) {
  const locale = mapLocale(lang);
  const pageParam = page ? `&page=${page}` : "";
  const data = await apiFetch(
    `/faqs?locale=${locale}&limit=100${pageParam}&depth=2`,
  );
  return toPaginated(data, (doc) => ({
    id: doc.id,
    uid: String(doc.id),
    data: {
      question: doc.question || "",
      answer: lexicalToPlainText(doc.answer),
    },
  }));
}

export async function getFaqsByPage(pageIdentifier, lang = "en-gb") {
  const locale = mapLocale(lang);
  const data = await apiFetch(
    `/faq-categories?locale=${locale}&where[pageIdentifier][equals]=${encodeURIComponent(pageIdentifier)}&limit=50&sort=order`,
  );

  return (data.docs || []).map((cat) => ({
    categoryTitle: cat.title || "",
    slug: cat.slug || String(cat.id),
    faqs: (cat.faqs || []).map((faq) => ({
      question: faq.question || "",
      answer: faq.answer || null,
      answerText: lexicalToPlainText(faq.answer),
      uid: faq.slug || String(faq.id),
      category: cat.title || "",
    })),
  }));
}

export async function getTeams(lang = "en-gb") {
  const locale = mapLocale(lang);
  const data = await apiFetch(`/teams?locale=${locale}&limit=50&depth=1`);

  const results = (data.docs || []).map((team) => ({
    id: String(team.id),
    uid: String(team.id),
    slug: team.slug || "",
    data: {
      category_name: team.name || "",
      team_element: (team.members || []).map((m) => ({
        id: String(m.id || Math.random()),
        name: m.name || "",
        title: m.role || "",
        description: lexicalToPlainText(m.bio) || "",
        image: mediaShape(m.image),
      })),
    },
  }));

  return {
    results,
    page: 1,
    total_pages: 1,
    results_per_page: 50,
    total_results_size: results.length,
  };
}

export async function getVideoSection(uid, lang = "en-gb") {
  const locale = mapLocale(lang);
  const data = await apiFetch(
    `/video-sections?locale=${locale}&where[slug][equals]=${encodeURIComponent(uid)}&limit=1&depth=2`,
  );
  const doc = data.docs?.[0];
  if (!doc) throw new Error(`VideoSection not found: ${uid}`);
  return transformVideoSection(doc);
}

export async function getAllEvents() {
  const data = await apiFetch("/events?sort=date&limit=19");
  return toPaginated(data, transformEvent);
}

export async function getEventsPage(page) {
  const data = await apiFetch(`/events?sort=date&limit=19&page=${page}`);
  return toPaginated(data, transformEvent);
}

export async function getAllTags(lang = "en-gb", type) {
  const locale = mapLocale(lang);
  let url = `/tags?locale=${locale}&limit=100`;
  if (type) {
    url += `&where[type][equals]=${encodeURIComponent(type)}`;
  }
  const data = await apiFetch(url);
  return (data.docs || [])
    .map((tag) => ({
      value: tag.value,
      label: tag.label || tag.value,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export async function getAllBlogContent(
  lang = "en-gb",
  page = 1,
  pageSize = 20,
  tags = [],
) {
  const locale = mapLocale(lang);
  const selectParams =
    "&select[title]=true&select[slug]=true&select[publishedAt]=true&select[updatedAt]=true&select[tags]=true&select[thumbnail]=true&select[meta]=true&select[feature]=true&select[description]=true&select[featureVideo]=true&select[episode_number]=true&select[time]=true";
  const [articles, episodes] = await Promise.all([
    apiFetch(`/articles?locale=${locale}&limit=200&depth=1${selectParams}`),
    apiFetch(`/episodes?locale=${locale}&limit=200&depth=1${selectParams}`),
  ]);
  let allDocs = [
    ...(articles.docs || []).map(transformArticle),
    ...(episodes.docs || []).map(transformEpisode),
  ].sort(
    (a, b) =>
      new Date(b.first_publication_date) - new Date(a.first_publication_date),
  );

  if (tags.length > 0) {
    allDocs = allDocs.filter((doc) => doc.tags?.some((t) => tags.includes(t)));
  }

  const start = (page - 1) * pageSize;
  return {
    results: allDocs.slice(start, start + pageSize),
    page,
    total_pages: Math.ceil(allDocs.length / pageSize) || 1,
    results_per_page: pageSize,
    total_results_size: allDocs.length,
  };
}

export async function getEvent(uid) {
  const data = await apiFetch(
    `/events?where[slug][equals]=${encodeURIComponent(uid)}&limit=1`,
  );
  const doc = data.docs?.[0];
  if (!doc) throw new Error(`Event not found: ${uid}`);
  return transformEvent(doc);
}
